import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, TrendingUp, Send, Info } from "lucide-react";
import { toast } from "sonner";
import api, { fmtMXN } from "../lib/api";
import { Trans, useTranslation } from "react-i18next";

export default function PriceChangeRequestModal({ supply, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [proposedPrice, setProposedPrice] = useState("");
  const [justification, setJustification] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const current = supply.unit_price;
  const newPrice = parseFloat(proposedPrice) || 0;
  const diff = newPrice - current;
  const pct = current > 0 ? ((diff / current) * 100).toFixed(1) : 0;

  const submit = async () => {
    if (!justification.trim()) {
      toast.error(t("priceModal.justifyError"));
      return;
    }
    if (!proposedPrice || newPrice <= 0) {
      toast.error(t("priceModal.diffError"));
      return;
    }
    if (Math.abs(newPrice - current) < 0.001) {
      toast.error(t("priceModal.diffError"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/price-changes", {
        supply_id: supply.supply_id,
        proposed_price: newPrice,
        reason: justification.trim(),
      });
      toast.success(t("priceModal.sentToast"));
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-[#FFDF00] flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#1A1A1A]" />
              </div>
              <div>
                <h2 className="font-display font-bold text-base">{t("priceModal.title")}</h2>
                <p className="text-xs text-slate-500">{supply.supply_name}</p>
              </div>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-xl hover:bg-slate-100 flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="bg-slate-50 rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("priceModal.currentPriceLabel")}</div>
              <div className="text-2xl font-display font-extrabold font-mono">{fmtMXN(current)}</div>
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">{t("priceModal.proposedPrice")}</label>
              <input data-testid="prc-input" type="number" step="0.01" min="0" value={proposedPrice}
                onChange={(e) => setProposedPrice(e.target.value)}
                className="w-full px-4 py-3 bg-white border-2 border-[#FFDF00] rounded-xl text-base font-mono font-bold focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
              {proposedPrice && (
                <div className={`mt-1.5 text-xs font-semibold ${diff > 0 ? "text-amber-700" : diff < 0 ? "text-emerald-700" : "text-slate-500"}`}>
                  {diff > 0 ? "+" : ""}{fmtMXN(diff)} ({pct}% vs actual)
                </div>
              )}
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">{t("priceModal.justification")}</label>
              <textarea data-testid="prc-reason" value={justification} onChange={(e) => setJustification(e.target.value)} rows={3}
                placeholder={t("priceModal.justificationPh")}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-900">
                <span className="font-bold">{t("priceModal.howApprovalWorks")}.</span>{" "}
                <Trans i18nKey="priceModal.howApprovalBody" components={{ strong: <strong /> }} />
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-100">
              {t("common.cancel")}
            </button>
            <button data-testid="prc-send-btn" onClick={submit} disabled={submitting}
              className="btn-primary-chizy px-5 py-2 rounded-xl text-sm flex items-center gap-1.5 disabled:opacity-50">
              <Send className="w-4 h-4" /> {submitting ? t("priceModal.sending") : t("priceModal.send")}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
